import React, { useState } from 'react';
import {
    Card,
    CardContent,
    CardActions,
    Button,
    Box,
    Avatar,
    Chip,
    Typography,
    IconButton,
    Collapse,
    Tooltip,
    ButtonGroup,
    Menu,
    MenuItem
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { FlashBetMatchDto } from '../models/dtos/FlashBetMatchDto';
import QuestionStatsModal from './QuestionStatsModal';

interface FlashBetMatchCardProps {
    match: FlashBetMatchDto;
    onEdit: (match: FlashBetMatchDto) => void;
    onEditScoreRecords: (match: FlashBetMatchDto) => void;
    onEditEvents: (match: FlashBetMatchDto) => void;
    onDelete: (match: FlashBetMatchDto) => void;
    onAddQuestion: (match: FlashBetMatchDto) => void;
    onEditQuestion: (match: FlashBetMatchDto, questionId: string) => void;
    onDeleteQuestion: (matchId: string, questionId: string) => void;
    onGenerateQuestions: (match: FlashBetMatchDto) => void;
    onPreviewAndGenerate: (match: FlashBetMatchDto) => void;
    onCopyBetsApiId: (betsApiId: string) => void;
    onShowError: (message: string) => void;
    generatingQuestions: string | null;
    copiedBetsApiId: string | null;
    defaultCollapsed?: boolean;
}

const FlashBetMatchCard: React.FC<FlashBetMatchCardProps> = ({
    match,
    onEdit,
    onEditScoreRecords,
    onEditEvents,
    onDelete,
    onAddQuestion,
    onEditQuestion,
    onDeleteQuestion,
    onGenerateQuestions,
    onPreviewAndGenerate,
    onCopyBetsApiId,
    onShowError,
    generatingQuestions,
    copiedBetsApiId,
    defaultCollapsed = false
}) => {
    const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
    const [showQuestionStats, setShowQuestionStats] = useState(false);
    const [generateMenuAnchor, setGenerateMenuAnchor] = useState<null | HTMLElement>(null);
    
    // Update internal state when defaultCollapsed prop changes
    React.useEffect(() => {
        setIsCollapsed(defaultCollapsed);
    }, [defaultCollapsed]);
    
    const handleGenerateMenuClick = (event: React.MouseEvent<HTMLElement>) => {
        setGenerateMenuAnchor(event.currentTarget);
    };

    const handleGenerateMenuClose = () => {
        setGenerateMenuAnchor(null);
    };

    const handlePreviewAndGenerate = () => {
        handleGenerateMenuClose();
        if (match.enabled) {
            onShowError('Match must be disabled before editing questions.');
            return;
        }
        onPreviewAndGenerate(match);
    };
    
    return (
        <Card
            sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative'
            }}
        >
            <CardContent sx={{ flexGrow: 1, p: isCollapsed ? 2 : 3 }}>
                {/* League and stadium information in top left corner, betsApiId in top right */}
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={isCollapsed ? 1 : 2}>
                    <Box>
                        <Typography variant="body2" component="span" sx={{ fontWeight: 'bold' }}>
                            {match.league?.name}
                        </Typography>
                        {match.stadium && (
                            <Typography variant="body2" component="span" sx={{ fontStyle: 'italic', ml: 1 }}>
                                ({match.stadium.name})
                            </Typography>
                        )}
                    </Box>
                    

                    
                    <Box display="flex" alignItems="center" gap={0.5}>
                        <Typography 
                            variant="body2" 
                            component="span" 
                            sx={{ 
                                fontWeight: 'bold', 
                                color: 'primary.main',
                                fontSize: '0.875rem'
                            }}
                        >
                            {match.betsApiId}
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={() => onCopyBetsApiId(match.betsApiId)}
                            sx={{ 
                                padding: 0.25,
                                '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08)' }
                            }}
                            title={copiedBetsApiId === match.betsApiId ? "Copied!" : "Copy BetsAPI ID"}
                        >
                            <ContentCopyIcon 
                                fontSize="small" 
                                sx={{ 
                                    color: copiedBetsApiId === match.betsApiId ? 'success.main' : 'action.active',
                                    fontSize: '16px'
                                }} 
                            />
                        </IconButton>
                    </Box>
                </Box>

                {isCollapsed ? (
                    /* Collapsed layout - more compact */
                    <Box>
                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                            <Box display="flex" alignItems="center" flex={1}>
                                <Avatar
                                    src={match.homeTeamDto?.photoUrl}
                                    alt={match.homeTeamDto?.name}
                                    sx={{ width: 32, height: 32, mr: 1.5 }}
                                />
                                <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>
                                    {match.homeTeamDto?.name}
                                </Typography>
                            </Box>

                            <Box display="flex" alignItems="center" flex={1} justifyContent="flex-end">
                                <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>
                                    {match.awayTeamDto?.name}
                                </Typography>
                                <Avatar
                                    src={match.awayTeamDto?.photoUrl}
                                    alt={match.awayTeamDto?.name}
                                    sx={{ width: 32, height: 32, ml: 1.5 }}
                                />
                            </Box>
                        </Box>

                        {/* Score, date, and counters centered together */}
                        <Box display="flex" flexDirection="column" alignItems="center" mb={1}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                {match.scoreHome} - {match.scoreAway}
                            </Typography>
                            {match.startDate && (
                                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                    {new Date(match.startDate).toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                    })}
                                </Typography>
                            )}
                            <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                                <Chip
                                    label={`📊 ${match.scoreRecordsCount}`}
                                    variant="outlined"
                                    size="small"
                                    onClick={() => onEditScoreRecords(match)}
                                    sx={{ 
                                        cursor: 'pointer',
                                        '&:hover': { 
                                            backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                            borderColor: 'primary.main'
                                        }
                                    }}
                                />
                                <Chip
                                    label={`📝 ${match.eventsCount}`}
                                    variant="outlined"
                                    size="small"
                                    onClick={() => onEditEvents(match)}
                                    sx={{ 
                                        cursor: 'pointer',
                                        '&:hover': { 
                                            backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                            borderColor: 'primary.main'
                                        }
                                    }}
                                />
                            </Box>
                        </Box>

                        {/* Status chips and expand button in same row */}
                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={0}>
                            <Box display="flex" alignItems="center" gap={1}>
                                <Chip
                                    label={match.enabled ? "Enabled" : "Disabled"}
                                    color={match.enabled ? "success" : "default"}
                                    size="small"
                                />
                                <Chip
                                    label={`${match.questions?.length || 0} Questions`}
                                    variant="outlined"
                                    size="small"
                                />
                                <Tooltip title="Total Games Played" arrow>
                                    <Chip
                                        label={`🎮 ${match.mainStats?.totalGames || 0}`}
                                        variant="outlined"
                                        size="small"
                                        sx={{ fontSize: '0.65rem', cursor: 'help' }}
                                    />
                                </Tooltip>
                                <Tooltip title="Total Won Games" arrow>
                                    <Chip
                                        label={`🏆 ${match.mainStats?.totalWonGames || 0}`}
                                        variant="outlined"
                                        size="small"
                                        sx={{ fontSize: '0.65rem', color: 'success.main', borderColor: 'success.main', cursor: 'help' }}
                                    />
                                </Tooltip>
                                <Tooltip title="Total Lost Games" arrow>
                                    <Chip
                                        label={`❌ ${match.mainStats?.totalLostGames || 0}`}
                                        variant="outlined"
                                        size="small"
                                        sx={{ fontSize: '0.65rem', color: 'error.main', borderColor: 'error.main', cursor: 'help' }}
                                    />
                                </Tooltip>
                                <Tooltip title="Total Expired Games" arrow>
                                    <Chip
                                        label={`⏱️ ${match.mainStats?.totalExpiredGames || 0}`}
                                        variant="outlined"
                                        size="small"
                                        sx={{ fontSize: '0.65rem', color: 'warning.main', borderColor: 'warning.main', cursor: 'help' }}
                                    />
                                </Tooltip>
                            </Box>

                            <Box display="flex" alignItems="center" gap={1}>
                                <Button
                                    variant="text"
                                    size="small"
                                    onClick={() => onEdit(match)}
                                    startIcon={<EditIcon />}
                                    sx={{
                                        backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                        '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08)' },
                                        borderRadius: 2,
                                        px: 1.5,
                                        minWidth: 'auto',
                                        color: 'primary.main'
                                    }}
                                >
                                    Edit
                                </Button>
                                <Button
                                    variant="text"
                                    size="small"
                                    onClick={() => setIsCollapsed(!isCollapsed)}
                                    startIcon={<ExpandMoreIcon />}
                                    sx={{
                                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' },
                                        borderRadius: 2,
                                        px: 1.5,
                                        minWidth: 'auto'
                                    }}
                                >
                                    Details
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                ) : (
                    /* Expanded layout - original design */
                    <Box>
                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                            <Box display="flex" alignItems="center" flex={1}>
                                <Avatar
                                    src={match.homeTeamDto?.photoUrl}
                                    alt={match.homeTeamDto?.name}
                                    sx={{ width: 48, height: 48, mr: 2 }}
                                />
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                    {match.homeTeamDto?.name}
                                </Typography>
                            </Box>

                            <Typography variant="h5" color="text.secondary" sx={{ mx: 4, fontWeight: 'bold' }}>
                                VS
                            </Typography>

                            <Box display="flex" alignItems="center" flex={1} justifyContent="flex-end">
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                    {match.awayTeamDto?.name}
                                </Typography>
                                <Avatar
                                    src={match.awayTeamDto?.photoUrl}
                                    alt={match.awayTeamDto?.name}
                                    sx={{ width: 48, height: 48, ml: 2 }}
                                />
                            </Box>
                        </Box>

                        {/* Start date and time centered below team names */}
                        {match.startDate && (
                            <Box display="flex" justifyContent="center" mb={2}>
                                <Typography variant="subtitle1" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                    {new Date(match.startDate).toLocaleDateString('en-US', {
                                        weekday: 'long',
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                    })}
                                </Typography>
                            </Box>
                        )}

                        {/* Score display centered below start date */}
                        <Box display="flex" justifyContent="center" mb={2}>
                            <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                {match.scoreHome} - {match.scoreAway}
                            </Typography>
                        </Box>
                    </Box>
                )}

                {!isCollapsed && (
                    <Box display="flex" alignItems="center" justifyContent="center" gap={2} mb={2}>
                        <Chip
                            label={match.enabled ? "Enabled" : "Disabled"}
                            color={match.enabled ? "success" : "default"}
                            size="small"
                        />
                    </Box>
                )}

                <Collapse in={!isCollapsed}>
                    {/* Stats badges below score */}
                    <Box display="flex" justifyContent="center" gap={1} mb={2}>
                        <Chip
                            label={`🎮 ${match.mainStats?.totalGames || 0} Games`}
                            variant="outlined"
                            size="small"
                        />
                        <Chip
                            label={`🏆 ${match.mainStats?.totalWonGames || 0} Won`}
                            variant="outlined"
                            size="small"
                            sx={{ color: 'success.main', borderColor: 'success.main' }}
                        />
                        <Chip
                            label={`❌ ${match.mainStats?.totalLostGames || 0} Lost`}
                            variant="outlined"
                            size="small"
                            sx={{ color: 'error.main', borderColor: 'error.main' }}
                        />
                        <Chip
                            label={`⏱️ ${match.mainStats?.totalExpiredGames || 0} Expired`}
                            variant="outlined"
                            size="small"
                            sx={{ color: 'warning.main', borderColor: 'warning.main' }}
                        />
                    </Box>

                    {/* Buy-in and payout amounts */}
                    <Box display="flex" justifyContent="center" gap={3} mb={3}>
                        <Typography 
                            variant="caption" 
                            color="text.secondary" 
                            sx={{ fontSize: '0.7rem' }}
                        >
                            💰 Total Buy-in: ${(match.mainStats?.totalBuyIn || 0).toLocaleString()}
                        </Typography>
                        <Typography 
                            variant="caption" 
                            color="text.secondary" 
                            sx={{ fontSize: '0.7rem' }}
                        >
                            💸 Total Payout: ${(match.mainStats?.totalPayout || 0).toLocaleString()}
                        </Typography>
                    </Box>

                    {/* AI Model and Prompt badges */}
                    <Box display="flex" justifyContent="center" gap={2} mb={2}>
                        <Chip
                            label={`🤖 Model: ${match.questionModelName || 'Default model'}`}
                            variant="outlined"
                            size="small"
                            sx={{ 
                                color: 'info.main', 
                                borderColor: 'info.main',
                                fontWeight: 500
                            }}
                        />
                        <Chip
                            label={`📝 Prompt: ${match.questionPromptName || 'Default prompt'}`}
                            variant="outlined"
                            size="small"
                            sx={{ 
                                color: 'info.main', 
                                borderColor: 'info.main',
                                fontWeight: 500
                            }}
                        />
                    </Box>

                    <Box mb={2}>
                        <Typography variant="subtitle2" gutterBottom>
                            Questions ({match.questions?.length || 0}):
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {match.questions?.map((question) => {
                                // Find the correct answer
                                const correctAnswer = question.question.answers?.find(
                                    answer => answer.id === question.correctAnswerId
                                );

                                return (
                                    <Box
                                        key={question.id}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                            borderRadius: 3,
                                            px: 2,
                                            py: 1,
                                            border: '1px solid rgba(0, 0, 0, 0.12)',
                                            transition: 'all 0.2s ease-in-out',
                                            '&:hover': {
                                                backgroundColor: 'rgba(0, 0, 0, 0.08)',
                                                borderColor: 'rgba(0, 0, 0, 0.2)',
                                            }
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                                            <Box
                                                sx={{
                                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                                    border: '1px solid #424242',
                                                    borderRadius: 2,
                                                    px: 1,
                                                    py: 0.25,
                                                    minWidth: 'fit-content'
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: '#424242',
                                                        fontWeight: 600,
                                                        fontSize: '0.65rem',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.5px'
                                                    }}
                                                >
                                                    {question.location.replace('_', '-')}
                                                </Typography>
                                            </Box>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: 500,
                                                    color: 'text.primary'
                                                }}
                                            >
                                                {question.question.title}
                                            </Typography>
                                            {correctAnswer && (
                                                <Box
                                                    sx={{
                                                        backgroundColor: 'rgba(46, 125, 50, 0.08)',
                                                        border: '1px solid #4caf50',
                                                        borderRadius: 2,
                                                        px: 1,
                                                        py: 0.25,
                                                        ml: 1
                                                    }}
                                                >
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: '#2e7d32',
                                                            fontWeight: 600,
                                                            fontSize: '0.7rem'
                                                        }}
                                                    >
                                                        {correctAnswer.name}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
                                            <IconButton
                                                size="small"
                                                onClick={() => onEditQuestion(match, question.id)}
                                                sx={{
                                                    padding: 0.5,
                                                    '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08)' }
                                                }}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    if (match.enabled) {
                                                        onShowError('Match must be disabled before editing questions.');
                                                        return;
                                                    }
                                                    onDeleteQuestion(match.id, question.id);
                                                }}
                                                sx={{
                                                    padding: 0.5,
                                                    '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.08)' }
                                                }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={() => {
                                        if (match.enabled) {
                                            onShowError('Match must be disabled before editing questions.');
                                            return;
                                        }
                                        onAddQuestion(match);
                                    }}
                                >
                                    Add Question
                                </Button>
                                <ButtonGroup
                                    variant="outlined"
                                    size="small"
                                    disabled={generatingQuestions === match.id}
                                >
                                    <Button
                                        startIcon={<AutoFixHighIcon />}
                                        onClick={() => {
                                            if (match.enabled) {
                                                onShowError('Match must be disabled before editing questions.');
                                                return;
                                            }
                                            onGenerateQuestions(match);
                                        }}
                                        sx={{
                                            '& .MuiSvgIcon-root': {
                                                background: 'linear-gradient(45deg, #FF6B6B, #4ECDC4, #45B7D1)',
                                                backgroundClip: 'text',
                                                WebkitBackgroundClip: 'text',
                                                WebkitTextFillColor: 'transparent'
                                            }
                                        }}
                                    >
                                        {generatingQuestions === match.id ? 'Generating...' : 'Generate Questions'}
                                    </Button>
                                    <Button
                                        size="small"
                                        onClick={handleGenerateMenuClick}
                                        sx={{ px: 0.5, minWidth: '32px' }}
                                    >
                                        <ArrowDropDownIcon />
                                    </Button>
                                </ButtonGroup>
                                <Menu
                                    anchorEl={generateMenuAnchor}
                                    open={Boolean(generateMenuAnchor)}
                                    onClose={handleGenerateMenuClose}
                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'right',
                                    }}
                                    transformOrigin={{
                                        vertical: 'top',
                                        horizontal: 'right',
                                    }}
                                >
                                    <MenuItem onClick={handlePreviewAndGenerate}>
                                        Preview & Generate
                                    </MenuItem>
                                </Menu>
                                {(match.mainStats?.totalGames || 0) > 0 && (
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<AnalyticsIcon />}
                                        onClick={() => setShowQuestionStats(true)}
                                        sx={{
                                            '& .MuiSvgIcon-root': {
                                                color: '#1976d2'
                                            }
                                        }}
                                    >
                                        Question Stats
                                    </Button>
                                )}
                            </Box>
                            
                            {/* Hide Details button in bottom-right */}
                            <Button
                                variant="text"
                                size="small"
                                onClick={() => setIsCollapsed(!isCollapsed)}
                                startIcon={<ExpandLessIcon />}
                                sx={{
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' },
                                    borderRadius: 2,
                                    px: 1.5,
                                    minWidth: 'auto'
                                }}
                            >
                                Hide
                            </Button>
                        </Box>
                    </Box>

                    {/* Tiny hints above footer actions */}
                    <Box sx={{ px: 0, pb: 1, pt: 0 }}>
                        <Box display="flex" justifyContent="center" gap={3}>
                            <Typography 
                                variant="caption" 
                                color="text.secondary" 
                                sx={{ 
                                    fontSize: '0.7rem',
                                    cursor: 'pointer',
                                    '&:hover': {
                                        color: 'primary.main',
                                        textDecoration: 'underline'
                                    }
                                }}
                                onClick={() => onEditScoreRecords(match)}
                            >
                                📊 Score Records: {match.scoreRecordsCount}
                            </Typography>
                            <Typography 
                                variant="caption" 
                                color="text.secondary" 
                                sx={{ 
                                    fontSize: '0.7rem',
                                    cursor: 'pointer',
                                    '&:hover': {
                                        color: 'primary.main',
                                        textDecoration: 'underline'
                                    }
                                }}
                                onClick={() => onEditEvents(match)}
                            >
                                📝 Events: {match.eventsCount}
                            </Typography>
                        </Box>
                    </Box>
                </Collapse>
            </CardContent>

            <Collapse in={!isCollapsed}>
                <CardActions sx={{ justifyContent: 'flex-start' }}>
                    <Button size="small" onClick={() => onEdit(match)}>
                        Edit Match
                    </Button>
                    <Button size="small" onClick={() => onEditScoreRecords(match)}>
                        Edit Score Records
                    </Button>
                    <Button size="small" onClick={() => onEditEvents(match)}>
                        Edit Events
                    </Button>
                    <Button
                        size="small"
                        color="error"
                        onClick={() => onDelete(match)}
                    >
                        Delete
                    </Button>
                </CardActions>
            </Collapse>

            <QuestionStatsModal
                open={showQuestionStats}
                onClose={() => setShowQuestionStats(false)}
                match={match}
            />
        </Card>
    );
};

export default FlashBetMatchCard;